// État de l'éditeur de schéma : historique (annuler / rétablir), enregistrement automatique, conflit de version.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ErreurApi } from '../../lib/api.ts';
import type { EtatEnregistrement } from '../../components/ui.tsx';
import type { Donnees, SchemaVue } from './modele.ts';

const LIMITE_HISTORIQUE = 150;
const DELAI_ENREGISTREMENT = 900;

interface Historique { passe: Donnees[]; present: Donnees; futur: Donnees[]; cle: string | null; quand: number }

export function useEditeurSchema(initial: SchemaVue, ecriture: boolean) {
  const [h, setH] = useState<Historique>({ passe: [], present: initial.data, futur: [], cle: null, quand: 0 });
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const [quand, setQuand] = useState<string | undefined>();
  const [conflit, setConflit] = useState<SchemaVue | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [corrections, setCorrections] = useState<SchemaVue['corrections']>([]);
  const [sources, setSources] = useState(initial.sources);
  const revision = useRef(initial.revision);
  const enregistre = useRef<Donnees>(initial.data);
  const enCours = useRef(false);
  const refusee = useRef<Donnees | null>(null);   // version refusée par le serveur (400) : pas de nouvel envoi tant qu'elle ne change pas
  const attente = useRef(0);                      // après une panne réseau : patienter avant de réessayer

  /**
   * Applique une modification. `cle` regroupe dans UNE entrée d'historique une suite de modifications du même geste
   * (déplacement à la souris, frappe dans un même champ) pour qu'« Annuler » défasse le geste entier.
   */
  const modifier = useCallback((fn: (d: Donnees) => Donnees, cle: string | null = null) => {
    if (!ecriture) return;
    setH((x) => {
      const suivant = fn(x.present);
      if (suivant === x.present) return x;
      const maintenant = Date.now();
      const fusion = cle !== null && cle === x.cle && maintenant - x.quand < 2500;
      return {
        passe: fusion ? x.passe : [...x.passe, x.present].slice(-LIMITE_HISTORIQUE),
        present: suivant, futur: [], cle, quand: maintenant,
      };
    });
  }, [ecriture]);

  const annuler = useCallback(() => setH((x) => x.passe.length
    ? { passe: x.passe.slice(0, -1), present: x.passe[x.passe.length - 1], futur: [x.present, ...x.futur], cle: null, quand: 0 } : x), []);
  const retablir = useCallback(() => setH((x) => x.futur.length
    ? { passe: [...x.passe, x.present], present: x.futur[0], futur: x.futur.slice(1), cle: null, quand: 0 } : x), []);

  const enregistrer = useCallback(async (donnees: Donnees) => {
    if (enCours.current) return;
    enCours.current = true;
    setEtat('en_cours');
    try {
      const r = await api.put<SchemaVue>(`/api/schemas/${initial.id}`, { revision: revision.current, data: donnees });
      revision.current = r.revision;
      enregistre.current = r.data;
      setSources(r.sources);
      setCorrections(r.corrections ?? []);
      setErreur(null);
      // Si rien n'a changé pendant l'envoi, on reprend la version normalisée par le serveur (compatibilités corrigées).
      setH((x) => (x.present === donnees ? { ...x, present: r.data } : x));
      setEtat('enregistre');
      setQuand(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
    } catch (e) {
      if (e instanceof ErreurApi && e.status === 409) {
        setConflit((e.details as any)?.courant ?? null);
        setEtat('modifie');
      } else if (e instanceof ErreurApi && e.status === 400) {
        refusee.current = donnees;
        setErreur(e.message);
        setEtat('erreur');
      } else {
        attente.current = Date.now() + 5000;
        setErreur(e instanceof ErreurApi ? e.message : 'Échec de l’enregistrement.');
        setEtat(e instanceof ErreurApi && e.code === 'hors_connexion' ? 'local' : 'erreur');
      }
    } finally {
      enCours.current = false;
      setH((x) => ({ ...x })); // relance l'effet : enregistre ce qui a changé pendant l'envoi, ou réessaie après une panne
    }
  }, [initial.id]);

  // Enregistrement automatique après une courte pause.
  useEffect(() => {
    if (!ecriture || conflit) return;
    if (h.present === enregistre.current || h.present === refusee.current) return;
    setEtat((e) => (e === 'en_cours' || e === 'erreur' || e === 'local' ? e : 'modifie'));
    const t = window.setTimeout(() => { void enregistrer(h.present); }, Math.max(DELAI_ENREGISTREMENT, attente.current - Date.now()));
    return () => window.clearTimeout(t);
  }, [h, ecriture, conflit, enregistrer]);

  // Changement d'onglet ou de page : les modifications en attente partent tout de suite (sans attendre la pause).
  const dernier = useRef({ present: h.present, conflit: !!conflit });
  dernier.current = { present: h.present, conflit: !!conflit };
  useEffect(() => () => {
    const { present, conflit: enConflit } = dernier.current;
    if (ecriture && !enConflit && present !== enregistre.current && present !== refusee.current) {
      void api.put(`/api/schemas/${initial.id}`, { revision: revision.current, data: present }).catch(() => {});
    }
  }, [ecriture, initial.id]);

  // Avertit avant de quitter la page avec des modifications non enregistrées.
  useEffect(() => {
    const avant = (e: BeforeUnloadEvent) => { if (h.present !== enregistre.current) e.preventDefault(); };
    window.addEventListener('beforeunload', avant);
    return () => window.removeEventListener('beforeunload', avant);
  }, [h.present]);

  /** Conflit : garder ma version (elle remplace la version enregistrée) ou reprendre la version enregistrée. */
  const garderMaVersion = useCallback(() => {
    if (!conflit) return;
    revision.current = conflit.revision;
    setConflit(null);
    void enregistrer(h.present);
  }, [conflit, enregistrer, h.present]);

  const reprendreVersionEnregistree = useCallback(() => {
    if (!conflit) return;
    revision.current = conflit.revision;
    enregistre.current = conflit.data;
    setSources(conflit.sources);
    setH({ passe: [], present: conflit.data, futur: [], cle: null, quand: 0 });
    setConflit(null);
    setEtat('enregistre');
  }, [conflit]);

  return {
    donnees: h.present, modifier, annuler, retablir, peutAnnuler: h.passe.length > 0, peutRetablir: h.futur.length > 0,
    etat, quand, erreur, corrections, sources, setSources, conflit, garderMaVersion, reprendreVersionEnregistree,
    enregistrerMaintenant: () => enregistrer(h.present),
  };
}
